import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ChefHat, Truck, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import PasswordInput from "../components/PasswordInput";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialRole = params.get("role") === "supplier" ? "supplier" : "restaurant";
  const [role, setRole] = useState(initialRole);
  const [form, setForm] = useState({
    email: "", password: "", name: "", business_name: "",
    phone: "", address: "", categories: [], paypal_email: "",
    vat_number: "", fiscal_code: "", sdi_code: "", pec_email: "",
    delivery_radius_km: 0,
  });
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clause, setClause] = useState(null);
  const [clauseAccepted, setClauseAccepted] = useState(false);
  const [clauseOpen, setClauseOpen] = useState(false);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCats(data)).catch(() => {});
    api.get("/legal/supplier-conformity-clause").then(({ data }) => setClause(data)).catch(() => {});
  }, []);

  const toggleCat = (id) => {
    setForm(f => ({
      ...f,
      categories: f.categories.includes(id)
        ? f.categories.filter(c => c !== id)
        : [...f.categories, id],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (role === "supplier" && form.categories.length === 0) {
      toast.error("Seleziona almeno una categoria");
      return;
    }
    if (role === "supplier" && !clauseAccepted) {
      toast.error("Devi accettare la clausola di conformità");
      return;
    }
    const vat = (form.vat_number || "").replace(/\s/g, "");
    if (!/^\d{11}$/.test(vat)) {
      toast.error("Partita IVA obbligatoria (11 cifre)");
      return;
    }
    const sdi = (form.sdi_code || "").trim();
    const pec = (form.pec_email || "").trim();
    if (!sdi && !pec) {
      toast.error("Inserisci il Codice Univoco SdI oppure la PEC");
      return;
    }
    if (sdi && sdi.length !== 7) {
      toast.error("Codice Univoco SdI: 7 caratteri alfanumerici");
      return;
    }
    setLoading(true);
    const r = await register({ ...form, vat_number: vat, sdi_code: sdi, pec_email: pec, role, conformity_clause_accepted: role === "supplier" ? true : undefined });
    setLoading(false);
    if (r.ok) {
      toast.success("Account creato!", {
        description: `Ti abbiamo inviato un'email per confermare l'indirizzo: apri il link entro 48 ore.${role === "supplier" ? " Potrai fare offerte dopo l'approvazione dell'amministratore." : ""}`,
        duration: 10000,
      });
      navigate(role === "supplier" ? "/fornitore" : "/ristorante");
    } else {
      toast.error(r.error);
    }
  };

  const roleBtn = (id, label, Icon) => (
    <button
      type="button"
      onClick={() => setRole(id)}
      data-active={role === id}
      className={`flex-1 flex items-center gap-3 rounded-2xl border p-4 transition-all ${role === id ? "border-orange-500 bg-orange-50/50" : "border-slate-200 hover:border-slate-300"}`}
      data-testid={`role-toggle-${id}`}
    >
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${role === id ? "text-white" : "text-slate-700 bg-slate-100"}`} style={role === id ? {background: "var(--ga-primary)"} : {}}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="text-left">
        <div className="font-semibold text-slate-900">{label}</div>
        <div className="text-xs text-slate-500">{id === "restaurant" ? "Ordini merce" : "Ricevi ordini"}</div>
      </div>
    </button>
  );

  return (
    <div className="mx-auto max-w-2xl px-4 py-12">
      <div className="ga-card p-8" data-testid="register-card">
        <h1 className="font-display text-3xl font-bold text-slate-900">Crea il tuo account</h1>
        <p className="mt-1 text-sm text-slate-600">Seleziona il tuo ruolo per iniziare.</p>

        <div className="mt-6 flex gap-3">
          {roleBtn("restaurant", "Acquirente", ChefHat)}
          {roleBtn("supplier", "Fornitore", Truck)}
        </div>

        <form onSubmit={submit} className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label htmlFor="business_name">{role === "restaurant" ? "Nome acquirente" : "Ragione sociale"}</Label>
            <Input id="business_name" required value={form.business_name} onChange={e => setForm({ ...form, business_name: e.target.value })} data-testid="register-business-name-input" placeholder={role === "restaurant" ? "Trattoria Da Mario" : "Ortofrutta Lombarda Srl"} />
          </div>
          <div>
            <Label htmlFor="name">Referente</Label>
            <Input id="name" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="register-name-input" placeholder="Mario Rossi" />
          </div>
          <div>
            <Label htmlFor="phone">Telefono</Label>
            <Input id="phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="register-phone-input" placeholder="+39 ..." />
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="register-email-input" />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <PasswordInput id="password" minLength={6} required value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} testId="register-password-input" autoComplete="new-password" />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="ref">Codice invito (opzionale)</Label>
            <Input id="ref" value={form.referral_code || ""} onChange={e => setForm({ ...form, referral_code: e.target.value.toUpperCase() })} placeholder="Se qualcuno ti ha invitato, inserisci il suo codice" data-testid="register-referral-input" />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="address">Indirizzo</Label>
            <Textarea id="address" rows={2} value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} data-testid="register-address-input" placeholder="Via, Città, CAP" />
          </div>

          {role === "supplier" && (
            <>
              <div className="sm:col-span-2">
                <Label>Categorie fornite</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {cats.map(c => (
                    <button type="button" key={c.id} onClick={() => toggleCat(c.id)} className="ga-pill" data-active={form.categories.includes(c.id)} data-testid={`register-category-${c.id}`}>
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="paypal_email">Email PayPal (per accrediti automatici)</Label>
                <Input id="paypal_email" type="email" value={form.paypal_email || ""} onChange={e => setForm({ ...form, paypal_email: e.target.value })} placeholder="pagamenti@tuo-fornitore.it" data-testid="register-paypal-email-input" />
                <div className="mt-1 text-xs text-slate-500">Ricevi automaticamente il pagamento su questo account PayPal quando l'ordine è consegnato.</div>
              </div>
              <div>
                <Label htmlFor="radius">Raggio consegna (km)</Label>
                <Input id="radius" type="number" min={0} step="1" value={form.delivery_radius_km} onChange={e => setForm({ ...form, delivery_radius_km: Number(e.target.value) })} placeholder="0 = illimitato" data-testid="register-radius-input" />
              </div>
              <div />
              <div className="sm:col-span-2 rounded-2xl border border-emerald-200 bg-emerald-50/40 p-4" data-testid="register-conformity-clause">
                <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-emerald-800">
                  <ShieldCheck className="w-3.5 h-3.5" /> Clausola di conformità · v{clause?.version || "1.0"}
                </div>
                <div className="mt-1 text-sm text-slate-700">
                  In qualità di fornitore ti impegni a consegnare <strong>esattamente</strong> la categoria e i prodotti specificati dall'acquirente. In caso di non conformità è previsto <strong>rimborso integrale, compensazione economica o sostituzione</strong> a spese del fornitore.
                </div>
                <button type="button" onClick={() => setClauseOpen(v => !v)} className="mt-2 text-xs font-semibold text-emerald-800 underline" data-testid="register-conformity-toggle">
                  {clauseOpen ? "Nascondi testo integrale" : "Leggi testo integrale"}
                </button>
                {clauseOpen && (
                  <div className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-emerald-200 bg-white p-3 text-xs text-slate-700 whitespace-pre-line" data-testid="register-conformity-full">
                    {clause?.text || "Caricamento…"}
                  </div>
                )}
                <label className="mt-3 flex items-start gap-2 cursor-pointer">
                  <Checkbox checked={clauseAccepted} onCheckedChange={(v) => setClauseAccepted(!!v)} data-testid="register-conformity-checkbox" />
                  <span className="text-sm text-slate-800">Ho letto e accetto integralmente la clausola di conformità.</span>
                </label>
              </div>
            </>
          )}
          <div className="sm:col-span-2 border-t border-slate-100 pt-4 mt-2">
            <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Dati fiscali <span className="text-red-600">*</span> obbligatori per fatturazione elettronica</div>
          </div>
          <div>
            <Label htmlFor="vat">Partita IVA <span className="text-red-600">*</span></Label>
            <Input id="vat" required inputMode="numeric" pattern="\d{11}" maxLength={11} value={form.vat_number} onChange={e => setForm({ ...form, vat_number: e.target.value.replace(/\D/g, "") })} placeholder="12345678901" data-testid="register-vat-input" />
            <div className="mt-1 text-xs text-slate-500">11 cifre, senza IT davanti.</div>
          </div>
          <div>
            <Label htmlFor="fc">Codice Fiscale</Label>
            <Input id="fc" value={form.fiscal_code} onChange={e => setForm({ ...form, fiscal_code: e.target.value.toUpperCase() })} placeholder="RSSMRA80A01H501U" data-testid="register-fc-input" />
          </div>
          <div>
            <Label htmlFor="sdi">Codice Univoco SdI <span className="text-red-600">*</span></Label>
            <Input id="sdi" maxLength={7} value={form.sdi_code} onChange={e => setForm({ ...form, sdi_code: e.target.value.toUpperCase() })} placeholder="ABCDE12" data-testid="register-sdi-input" />
            <div className="mt-1 text-xs text-slate-500">7 caratteri. Se non lo hai, compila la PEC.</div>
          </div>
          <div>
            <Label htmlFor="pec">PEC <span className="text-red-600">*</span></Label>
            <Input id="pec" type="email" value={form.pec_email} onChange={e => setForm({ ...form, pec_email: e.target.value })} placeholder="azienda@pec.it" data-testid="register-pec-input" />
            <div className="mt-1 text-xs text-slate-500">Obbligatoria se non hai il Codice SdI.</div>
          </div>

          <div className="sm:col-span-2 text-xs text-slate-500" data-testid="register-verification-note">
            Dopo la registrazione ricevi un'email per confermare l'indirizzo. Controlliamo la Partita IVA su VIES: i fornitori possono fare offerte dopo l'approvazione dell'amministratore; chi non risulta su VIES carica la visura camerale dal proprio account. La Partita IVA non si potrà modificare dal profilo.
          </div>
          <div className="sm:col-span-2 mt-2">
            <Button type="submit" disabled={loading} className="w-full ga-btn-primary rounded-full" data-testid="register-submit-button">
              {loading ? "Creazione..." : "Crea account"}
            </Button>
          </div>
        </form>

        <div className="mt-6 text-sm text-slate-600 text-center">
          Hai già un account? <Link to="/login" className="font-semibold text-slate-900 hover:underline" data-testid="register-to-login-link">Accedi</Link>
        </div>
      </div>
    </div>
  );
}
